/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useMemo, useRef, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Content data
// ---------------------------------------------------------------------------

interface Section {
  id: string;
  title: string;
  icon: string;
  content: string;
  tags: string[];
}

const sections: Section[] = [
  // ── Overview ──
  {
    id: 'overview',
    title: 'Overview',
    icon: '📖',
    tags: ['overview', 'introduction', 'what is', 'about'],
    content: `DataMave Formulary is a web-based pharmacist decision tool for assigning
taxonomy codes to compounding formulas. Each formula describes a compound
(ingredients, dosage form, strength, schedule) that must be mapped to one or
more Healthcare Provider Taxonomy codes so the compound can be referenced in
insurance and regulatory workflows.

**Key concepts:**
- **Formula** — a compounding recipe with ingredients, dosage form, strength,
  and DEA schedule.
- **Taxonomy Code** — a Healthcare Provider Taxonomy code describing a
  provider specialty (e.g., "122300000X — Dentist").
- **Assignment** — a link between a formula and a taxonomy code. A formula can
  have multiple assignments.
- **Suggestion** — the system recommends taxonomy codes based on ingredients
  shared with already-assigned formulas.`,
  },

  // ── Roles ──
  {
    id: 'roles',
    title: 'Roles & Permissions',
    icon: '🔐',
    tags: ['roles', 'permissions', 'access', 'pharmacist', 'manager', 'admin'],
    content: `Access is controlled by Entra ID (Azure AD) with three application roles:

| Role | Formulas | Assignments | Dashboard | Settings |
|------|----------|-------------|-----------|----------|
| **Pharmacist** | View, filter, search | Create, reassign, remove | — | Yes |
| **Manager** | View, filter, search | Create, reassign, remove | View metrics | Yes |
| **Admin** | View, filter, search | Create, reassign, remove | View metrics | Yes |

Managers and Admins see the Dashboard page with team-wide metrics. All roles
can use the Taxonomy browser and Settings page.`,
  },

  // ── Formula Queue ──
  {
    id: 'formula-queue',
    title: 'Formula Queue',
    icon: '📋',
    tags: ['formula', 'queue', 'list', 'filter', 'search', 'ingredient', 'formula only', 'ingredient only', 'scope', 'clear', 'batch', 'select'],
    content: `The Formula Queue (\`/formulas\`) is the main work screen. It shows a
paginated table of all formulas with status indicators.

**Status badges:**
- 🟡 **Unassigned** — no taxonomy codes linked yet (needs work).
- 🟢 **Assigned** — at least one taxonomy code linked.

**Filtering:**
- **Status filter** — show All, Unassigned only, or Assigned only.
- **Dosage Form** — filter by form (Cream, Capsule, Solution, etc.).
- **Schedule** — filter by DEA schedule.
- **Search** — a single text field that searches both formula names and
  ingredient names at the same time by default. Matching ingredients are
  highlighted in yellow.
- **Ingredient Only / Formula Only** — two stacked checkboxes next to the
  search field restrict the scope of the search. Only one may be active at a
  time; leave both unchecked to search both formula names and ingredients.
- **Clear** — resets status, dosage form, schedule, search text, search
  scope, and sort to their defaults in one click.

**Selection & batch operations:**
- Check the box on unassigned formulas to select them.
- Use "Select all" to select all visible unassigned formulas.
- When a search or any filter is active, an "Expand to all" prompt lets you
  select every matching formula across all pages. The selected count will
  match the displayed total because the same status, dosage form, schedule,
  and search scope are applied.
- Selected count appears in the header and unlocks the Batch Assign bar.`,
  },

  // ── Single Assignment ──
  {
    id: 'single-assign',
    title: 'Single Assignment',
    icon: '🎯',
    tags: ['assign', 'single', 'detail', 'search', 'browse', 'suggest', 'taxonomy'],
    content: `Click any formula row to open the detail modal (or navigate to
\`/formulas/:setId\`). The assignment screen has three sections:

**Top section:**
- Formula properties (dosage form, strength, schedule, routes, source, sterile flag).
- Current assignments — each shows the taxonomy code and display name with a
  Remove button.
- Ingredients — displayed as purple pills.

**Assignment methods:**
1. **Quick search** — type a taxonomy code or name in the search box. Results
   appear in a dropdown; click to assign.
2. **Browse** — the taxonomy browser shows all codes with cascading filters
   (Section → Grouping → Classification → Specialization). Click a code in
   the left pane to see details on the right, then click "Assign" to link it.
3. **Suggested tab** — if the formula shares ingredients with already-assigned
   formulas, the browser's "Suggested" tab shows those taxonomy codes ranked
   by shared ingredient count.

**Reassign:** click "Reassign" on an existing assignment to enter replacement
mode. The old code is removed and the search switches to find the replacement.

**Remove:** click "Remove" on an assignment, confirm in the dialog, and the
link is archived (version chain preserved).`,
  },

  // ── Batch Assignment ──
  {
    id: 'batch-assign',
    title: 'Batch Assignment',
    icon: '⚡',
    tags: ['batch', 'bulk', 'multiple', 'assign', 'progress', 'sse'],
    content: `Batch assignment lets you assign one or more taxonomy codes to many
formulas at once.

**Step-by-step:**
1. Filter the formula list (e.g., by ingredient or dosage form).
2. Select the formulas you want to assign (checkboxes).
3. Click **"Batch Assign Taxonomy"** in the bar that appears.
4. A picker modal opens — browse or search for taxonomy codes.
5. Select one or more codes (they appear as chips at the top).
6. Click **"Done Assigning"** to confirm your selection.
7. Click the green **"Assign X code(s) → Y formula(s)"** button.
8. A progress overlay shows real-time progress via Server-Sent Events (SSE).
9. On completion, the list auto-refreshes and selections clear.

**Tips:**
- You can assign multiple taxonomy codes in a single batch.
- Only unassigned formulas can be selected for batch assignment.
- If a formula already has the taxonomy code, it will be skipped (no duplicates).
- The progress bar shows success/failure counts in real time.`,
  },

  // ── Taxonomy Browser ──
  {
    id: 'taxonomy-browser',
    title: 'Taxonomy Browser',
    icon: '🏷️',
    tags: ['taxonomy', 'browse', 'filter', 'section', 'grouping', 'classification', 'specialization', 'reference'],
    content: `The Taxonomy page (\`/taxonomy\`) provides a read-only reference browser
for all Healthcare Provider Taxonomy codes.

**Features:**
- **Search** — filter by code or display name.
- **Cascading filters** — Section, Grouping, Classification, and
  Specialization dropdowns. Child filters update when a parent changes.
- **Expandable rows** — click a row to see the full Definition and Notes.
- **Pagination** — 50 codes per page with Previous/Next navigation.

The same taxonomy browser component is embedded in the Assignment page
and Batch Assignment picker, with additional features:
- **Suggested tab** — shows codes recommended based on shared ingredients.
- **Assign button** — directly assigns the selected code.
- **Exclude list** — already-assigned codes are grayed out.`,
  },

  // ── Dashboard ──
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: '📊',
    tags: ['dashboard', 'metrics', 'coverage', 'productivity', 'activity', 'chart', 'team'],
    content: `The Dashboard (\`/dashboard\`) is available to Managers and Admins. It
shows coverage metrics and team productivity.

**Hero cards (top row):**
- **Total Formulas** — total count in the system.
- **Assigned** — count and percentage of assigned formulas.
- **Unassigned** — count and percentage needing work.
- **Coverage** — circular progress indicator with color coding
  (green ≥75%, amber 50–75%, red <50%).

**Assignment Activity** — bar chart showing daily assignment volume for the
selected period (7d / 14d / 30d / 90d).

**Team Productivity** — leaderboard ranking pharmacists by total actions
(assignments + reassignments + removals). Top 3 get medal badges.

**Recent Activity** — time-sorted table of recent actions with user, action
type badge, formula name, and notes.`,
  },

  // ── Smart Suggestions ──
  {
    id: 'suggestions',
    title: 'Smart Suggestions',
    icon: '🧠',
    tags: ['suggestion', 'smart', 'recommend', 'ingredient', 'shared', 'match'],
    content: `When you open a formula for assignment, the system analyzes its
ingredients and finds other formulas that share those ingredients. Any taxonomy
codes assigned to those related formulas are surfaced as suggestions.

**How matching works:**
- Ingredient names are matched by prefix (e.g., "PROPYLENE GLYCOL" matches
  "PROPYLENE GLYCOL USP PCCA").
- The "shared ingredient count" badge shows how many ingredient matches exist.
- Suggestions are ranked by match count (most shared first).

**Using suggestions:**
1. Open any formula (click the row or navigate to its page).
2. In the taxonomy browser, switch to the **Suggested** tab.
3. Review the suggested codes — each shows its shared ingredient count.
4. Click an appropriate code to assign it.

Suggestions improve as more formulas are assigned — the more assignments exist
in the system, the better the recommendations become.`,
  },

  // ── Settings ──
  {
    id: 'settings',
    title: 'Settings',
    icon: '⚙️',
    tags: ['settings', 'preferences', 'page size', 'startup', 'claim'],
    content: `The Settings page (\`/settings\`) lets you configure personal preferences:

- **Default Page Size** — how many formulas to show per page (25, 50, or 100).
- **Startup View** — which page to show when you first log in (Formula Queue or Dashboard).
- **Claim Batch Size** — how many formulas to claim at once when using the
  claim feature (1–50, default 10).

Settings are saved per user and persist across sessions.`,
  },

  // ── Keyboard Shortcuts ──
  {
    id: 'keyboard',
    title: 'Keyboard Shortcuts',
    icon: '⌨️',
    tags: ['keyboard', 'shortcut', 'hotkey', 'ctrl', 'alt', 'esc'],
    content: `**Formula Detail Modal:**
| Shortcut | Action |
|----------|--------|
| \`Esc\` | Close the detail modal |
| \`Alt + ←\` | Navigate to the previous formula |
| \`Alt + →\` | Navigate to the next formula |

**Assignment Page:**
| Shortcut | Action |
|----------|--------|
| \`Ctrl + Enter\` | Advance to the next unassigned formula |

**General:**
| Shortcut | Action |
|----------|--------|
| \`Tab\` | Navigate between interactive elements |
| \`Enter\` | Activate the focused button or link |`,
  },

  // ── Use Case: New Formula Triage ──
  {
    id: 'uc-triage',
    title: 'Use Case: New Formula Triage',
    icon: '📥',
    tags: ['use case', 'triage', 'new', 'workflow', 'unassigned'],
    content: `**Scenario:** A new batch of formulas has been imported and needs taxonomy
assignment.

**Workflow:**
1. Navigate to the **Formula Queue** and set the status filter to
   **Unassigned**.
2. Review each formula's name, dosage form, and ingredients.
3. Click a formula to open the detail modal.
4. Check the **Suggested** tab — if suggestions exist, review and assign
   the appropriate code(s).
5. If no suggestions, use the **Browse** tab or **Search** to find the
   correct taxonomy code.
6. After assigning, press \`Alt + →\` to move to the next formula.
7. Repeat until all formulas in view are assigned.

**Tip:** If many formulas share the same ingredient (e.g., TESTOSTERONE),
use the ingredient search to find them all, then batch-assign the appropriate
taxonomy code.`,
  },

  // ── Use Case: Batch by Ingredient ──
  {
    id: 'uc-batch-ingredient',
    title: 'Use Case: Batch Assign by Ingredient',
    icon: '🧪',
    tags: ['use case', 'batch', 'ingredient', 'bulk', 'workflow'],
    content: `**Scenario:** You know that all formulas containing ESTRADIOL should be
assigned to a specific set of taxonomy codes.

**Workflow:**
1. In the Formula Queue, type **"ESTRADIOL"** in the search box.
2. Tick the **"Ingredient Only"** checkbox next to the search field so the
   search is restricted to ingredient names (not formula names).
3. Set the status filter to **Unassigned** to focus on formulas needing work.
4. Click **"Select all"** to check every visible formula.
5. If more results exist beyond the current page, click **"Select all X
   matches"** to expand the selection across all pages.
6. Click **"Batch Assign Taxonomy"** in the blue bar.
7. In the picker, search for or browse to the correct taxonomy code(s).
8. Select one or more codes and click **"Done Assigning"**.
9. Click the green **"Assign"** button and watch the progress bar.
10. The list auto-refreshes showing the newly assigned formulas.

**Tip:** to find compounds by their *product name* instead of ingredient,
tick **"Formula Only"** instead. Leaving both checkboxes unchecked searches
both formula names and ingredient names simultaneously.`,
  },

  // ── Use Case: Reassignment ──
  {
    id: 'uc-reassign',
    title: 'Use Case: Correcting a Wrong Assignment',
    icon: '🔄',
    tags: ['use case', 'reassign', 'correct', 'fix', 'wrong', 'replace'],
    content: `**Scenario:** A formula was assigned to the wrong taxonomy code and needs
correction.

**Workflow:**
1. Find the formula in the Formula Queue (use search or set status to
   **Assigned**).
2. Click the formula to open the detail modal.
3. In the **Current Assignments** section, locate the incorrect assignment.
4. Click **"Remove"** and confirm in the dialog.
5. The assignment is archived (not deleted — the audit trail is preserved).
6. Now use the taxonomy browser or search to find and assign the correct
   code.

**Alternative (Reassign flow):**
1. Click **"Reassign"** on the incorrect assignment.
2. The interface switches to replacement mode.
3. Search for or browse to the correct taxonomy code.
4. Click to assign — the old code is automatically replaced.`,
  },

  // ── Use Case: Coverage Review ──
  {
    id: 'uc-coverage',
    title: 'Use Case: Monitoring Team Progress',
    icon: '📈',
    tags: ['use case', 'coverage', 'progress', 'dashboard', 'manager', 'team'],
    content: `**Scenario:** As a manager, you want to track how much of the formula
catalog has been assigned and who is doing the work.

**Workflow:**
1. Navigate to the **Dashboard** (available to Manager and Admin roles).
2. Check the **Coverage** card — it shows the percentage of formulas that
   have at least one taxonomy code assigned.
3. Review the **Assignment Activity** chart to see daily trends. Switch between
   7-day, 14-day, 30-day, and 90-day views.
4. Check the **Team Productivity** leaderboard to see which pharmacists are
   most active.
5. Scroll down to **Recent Activity** to see the latest individual actions.

**Tip:** If coverage is low, use the ingredient-based batch assignment workflow
to rapidly assign codes to groups of related formulas.`,
  },

  // ── Glossary ──
  {
    id: 'glossary',
    title: 'Glossary',
    icon: '📚',
    tags: ['glossary', 'definition', 'term', 'concept'],
    content: `| Term | Definition |
|------|-----------|
| **Formula** | A compounding recipe describing a medication to be prepared by a pharmacy. |
| **Taxonomy Code** | A Healthcare Provider Taxonomy (HPT) code that classifies a healthcare provider's specialty. |
| **Assignment** | A link (x_link) between a formula and a taxonomy code. |
| **Set ID** | A unique identifier grouping all versions of the same record. |
| **Version Chain** | The sequence of versions for a record (set_id + version_num). |
| **Ingredient** | A chemical, drug, device, or formula used as a component in a compound. |
| **Dosage Form** | The physical form of the medication (cream, capsule, solution, etc.). |
| **DEA Schedule** | Drug Enforcement Administration classification (Schedule 2–5) for controlled substances. |
| **Coverage** | The percentage of formulas with at least one taxonomy code assigned. |
| **Depreciation Flag** | A clinical flag indicating an item is off-market or no longer referenced. |
| **Archive Flag** | Indicates a record has been soft-deleted (e.g., a removed assignment). |
| **SSE** | Server-Sent Events — a real-time streaming protocol used for batch progress updates. |`,
  },
];

// ---------------------------------------------------------------------------
// Markdown-like rendering (simple subset)
// ---------------------------------------------------------------------------

function renderContent(raw: string): JSX.Element {
  const lines = raw.trim().split('\n');
  const elements: JSX.Element[] = [];
  let tableRows: string[][] = [];
  let inTable = false;

  function flushTable() {
    if (tableRows.length < 2) { tableRows = []; inTable = false; return; }
    const headers = tableRows[0];
    const body = tableRows.slice(2); // skip separator row
    elements.push(
      <div key={`tbl-${elements.length}`} className="overflow-x-auto my-3">
        <table className="min-w-full text-sm border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-1.5 text-left font-medium text-gray-700 border-b border-gray-200">{renderInline(h.trim())}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-1.5 text-gray-600 border-b border-gray-100">{renderInline(cell.trim())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    inTable = false;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table row
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      inTable = true;
      const cells = line.trim().slice(1, -1).split('|');
      tableRows.push(cells);
      continue;
    }

    if (inTable) flushTable();

    // Empty line
    if (!line.trim()) {
      continue;
    }

    // Heading
    if (line.startsWith('**') && line.endsWith('**') && !line.includes('—')) {
      elements.push(<h4 key={i} className="font-semibold text-gray-900 mt-3 mb-1">{line.replace(/\*\*/g, '')}</h4>);
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line.trim())) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2 text-gray-600">
          <span className="text-dds-blue-500 font-medium shrink-0">{line.trim().match(/^\d+/)?.[0]}.</span>
          <span>{renderInline(line.trim().replace(/^\d+\.\s/, ''))}</span>
        </div>
      );
      continue;
    }

    // Bullet list
    if (line.trim().startsWith('- ')) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2 text-gray-600">
          <span className="text-dds-blue-400 shrink-0">•</span>
          <span>{renderInline(line.trim().slice(2))}</span>
        </div>
      );
      continue;
    }

    // Regular paragraph
    elements.push(<p key={i} className="text-gray-600 leading-relaxed">{renderInline(line)}</p>);
  }

  if (inTable) flushTable();

  return <div className="space-y-1">{elements}</div>;
}

function renderInline(text: string): JSX.Element {
  // Handle **bold**, `code`, and plain text
  const parts: JSX.Element[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${match.index}`} className="font-semibold text-gray-800">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={`c-${match.index}`} className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono text-dds-blue-600">{token.slice(1, -1)}</code>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(<span key={`e-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }

  return <>{parts}</>;
}

// ---------------------------------------------------------------------------
// HelpPage component
// ---------------------------------------------------------------------------

export function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Filter sections by search
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.content.toLowerCase().includes(q) ||
        s.tags.some((t) => t.includes(q))
    );
  }, [searchQuery]);

  // Highlight matched text
  function highlightContent(section: Section) {
    const rendered = renderContent(section.content);
    if (!searchQuery.trim()) return rendered;
    return rendered;
  }

  // Scroll to section
  function scrollToSection(id: string) {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [filteredSections]);

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Table of Contents — left sidebar */}
      <aside className="w-64 shrink-0 border-r border-gray-200 bg-gray-50/50 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Contents</h3>

          {/* Search */}
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-dds-blue-400 focus:border-dds-blue-400"
            />
            <svg className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* TOC list */}
          <nav className="space-y-0.5">
            {filteredSections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 text-sm rounded transition-colors ${
                  activeSection === s.id
                    ? 'bg-dds-blue-50 text-dds-blue-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="text-sm">{s.icon}</span>
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </nav>

          {filteredSections.length === 0 && (
            <p className="text-sm text-gray-400 text-center mt-4">No results for "{searchQuery}"</p>
          )}

          {searchQuery && filteredSections.length > 0 && (
            <p className="text-xs text-gray-400 mt-3 text-center">
              {filteredSections.length} of {sections.length} sections
            </p>
          )}
        </div>
      </aside>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">DataMave Formulary Documentation</h1>
            <p className="text-sm text-gray-500 mt-1">User guide, workflows, and reference for the Formulary Assignment Tool.</p>
          </div>

          <div className="space-y-10">
            {filteredSections.map((s) => (
              <div
                key={s.id}
                id={s.id}
                ref={(el) => { sectionRefs.current[s.id] = el; }}
                className="scroll-mt-6"
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <span className="text-lg">{s.icon}</span>
                  <h2 className="text-lg font-semibold text-gray-900">{s.title}</h2>
                </div>
                {highlightContent(s)}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
            <p>© 2026 Dobler Data Solutions, LLC. All rights reserved.</p>
            <p className="mt-1">DataMave Formulary v1.0</p>
          </div>
        </div>
      </main>
    </div>
  );
}
